import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit3, Play, Shield, AlertTriangle, RefreshCw, ChevronDown, ChevronUp, X, Check } from 'lucide-react';
import { useApi } from './hooks/useApi';

// ── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { id: 'all',                label: 'All',             icon: null,  isSecure: false },
  { id: 'common',             label: 'Common Support',  icon: null,  isSecure: false },
  { id: 'edge_case',          label: 'Edge Cases',      icon: null,  isSecure: false },
  { id: 'security_pii',       label: 'PII Extraction',  icon: '🔒', isSecure: true  },
  { id: 'security_injection', label: 'Prompt Injection', icon: '🔒', isSecure: true  },
  { id: 'security_policy',    label: 'Policy Boundary', icon: '🔒', isSecure: true  },
  { id: 'security_kb',        label: 'KB Boundary',     icon: '🔒', isSecure: true  },
];

const PRIORITY_META = {
  urgent: { label: 'Urgent', color: '#ef4444' },
  high:   { label: 'High',   color: '#f97316' },
  normal: { label: 'Normal', color: '#6b7280' },
  low:    { label: 'Low',    color: '#9ca3af' },
};

const OUTCOME_META = {
  resolved:   { label: 'Resolved',   color: '#10b981' },
  escalated:  { label: 'Escalated',  color: '#f97316' },
  unresolved: { label: 'Unresolved', color: '#ef4444' },
};

const RESULT_META = {
  passed: { label: 'Passed', color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  failed: { label: 'Failed', color: '#ef4444', bg: 'rgba(239,68,68,0.1)'  },
};

// ── Sub-components ───────────────────────────────────────────────────────────

function Badge({ label, color, bg }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
      color, backgroundColor: bg || `${color}18`,
      border: `1px solid ${color}40`,
    }}>
      {label}
    </span>
  );
}

// ── Slide-out results drawer ─────────────────────────────────────────────────

function ScenarioResultDrawer({ result, scenarioName, onClose }) {
  const [visible, setVisible] = useState(false);

  // Trigger slide-in after mount so the CSS transition fires
  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 220); // wait for slide-out animation
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const evalStatus = result?.evaluation?.status;
  const ev         = result?.evaluation?.evaluation || {};
  const sim        = result?.simulation || {};
  const passed     = result?.result === 'passed';
  const evalFailed = evalStatus === 'error' || (evalStatus === 'success' && !ev.accuracy);
  const metricColor = (v) => v >= 4 ? '#10b981' : v >= 3 ? '#f59e0b' : '#ef4444';

  const turns = sim.turns || sim.messages || sim.conversation || [];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 1100,
          background: 'rgba(0,0,0,0.45)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.2s ease',
        }}
      />

      {/* Drawer panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 520, maxWidth: '92vw',
        zIndex: 1101,
        background: '#0f0f23',
        borderLeft: `1px solid ${passed ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'}`,
        display: 'flex', flexDirection: 'column',
        transform: visible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.5)',
      }}>

        {/* ── Drawer header ── */}
        <div style={{
          padding: '14px 20px',
          background: passed ? 'rgba(16,185,129,0.06)' : 'rgba(239,68,68,0.06)',
          borderBottom: '1px solid #1a1a2e',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 4 }}>
                SCENARIO RESULT
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {scenarioName}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: passed ? '#10b981' : '#ef4444' }}>
                  {passed ? '✓ Passed' : '✗ Failed'} · {ev.overall_score ?? ev.overall ?? '—'}/100
                </span>
                {ev.kb_grounded && (
                  <span style={{ fontSize: 11, color: '#a78bfa' }}>📚 KB-grounded</span>
                )}
                {sim.mode === 'live_endpoint' && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#38bdf8', background: 'rgba(56,189,248,0.1)', padding: '1px 8px', borderRadius: 8, border: '1px solid rgba(56,189,248,0.25)' }}>
                    🔗 Live endpoint
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={handleClose}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 4, flexShrink: 0, borderRadius: 4 }}
              onMouseEnter={e => e.currentTarget.style.color = '#e2e8f0'}
              onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
              title="Close (Esc)"
            >
              <X size={18} />
            </button>
          </div>

          {/* Outcome / containment / turns strip */}
          <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#6b7280' }}>
              Outcome:{' '}
              <span style={{
                color: sim.outcome === 'resolved' ? '#10b981' : sim.outcome === 'escalated' ? '#f97316' : '#ef4444',
                fontWeight: 600,
              }}>
                {sim.outcome ? sim.outcome.charAt(0).toUpperCase() + sim.outcome.slice(1) : '—'}
              </span>
            </span>
            <span style={{ fontSize: 11, color: '#6b7280' }}>
              Containment:{' '}
              <span style={{ color: sim.containment ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                {sim.containment ? 'Yes' : 'No'}
              </span>
            </span>
            <span style={{ fontSize: 11, color: '#6b7280' }}>
              Turns:{' '}
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{turns.length || '—'}</span>
            </span>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ flex: 1, overflowY: 'scroll', padding: '20px' }}>

          {/* Evaluation error banner */}
          {evalFailed && (
            <div style={{
              padding: '10px 14px', fontSize: 12, color: '#f59e0b',
              background: 'rgba(245,158,11,0.06)', borderRadius: 8,
              border: '1px solid rgba(245,158,11,0.2)',
              display: 'flex', alignItems: 'center', gap: 8,
              marginBottom: 16,
            }}>
              <span>⚠️</span>
              <span>
                {result?.evaluation?.error
                  ? `Evaluation error: ${result.evaluation.error}`
                  : 'Scores unavailable — the evaluation step did not return data. The simulation ran successfully.'}
              </span>
            </div>
          )}

          {/* ── Metric scores ── */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', letterSpacing: '0.07em', marginBottom: 10 }}>
              SCORES
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {['accuracy', 'tone', 'resolution', 'efficiency'].map(m => (
                <div key={m} style={{
                  background: '#1a1a2e', borderRadius: 8, padding: '10px 14px',
                  border: '1px solid #2d2d4e',
                }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: metricColor(ev[m] || 0) }}>
                    {ev[m]?.toFixed(1) ?? '—'}<span style={{ fontSize: 11, color: '#6b7280' }}>/5</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'capitalize', marginTop: 2 }}>{m}</div>
                </div>
              ))}
            </div>

            {/* Groundedness */}
            {ev.groundedness !== undefined && (
              <div style={{
                background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)',
                borderRadius: 8, padding: '10px 14px', marginTop: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11, color: '#a78bfa', fontWeight: 600 }}>Groundedness</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: metricColor(ev.groundedness) }}>
                    {ev.groundedness?.toFixed(1)}/5
                  </span>
                </div>
                {ev.hallucination_detected && (
                  <div style={{ fontSize: 11, color: '#ef4444', fontWeight: 600, marginTop: 4 }}>⚠️ Hallucination detected</div>
                )}
              </div>
            )}
          </div>

          {/* ── Strengths & Improvements ── */}
          {(ev.strengths?.length > 0 || ev.improvements?.length > 0) && (
            <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {ev.strengths?.length > 0 && (
                <div style={{
                  background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.15)',
                  borderRadius: 8, padding: '12px 14px',
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#10b981', marginBottom: 8, letterSpacing: '0.07em' }}>
                    💪 STRENGTHS
                  </div>
                  <ul style={{ margin: 0, padding: '0 0 0 15px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {ev.strengths.map((s, i) => (
                      <li key={i} style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.55 }}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {ev.improvements?.length > 0 && (
                <div style={{
                  background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)',
                  borderRadius: 8, padding: '12px 14px',
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', marginBottom: 8, letterSpacing: '0.07em' }}>
                    🎯 IMPROVEMENTS
                  </div>
                  <ul style={{ margin: 0, padding: '0 0 0 15px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {ev.improvements.map((s, i) => (
                      <li key={i} style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.55 }}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* ── Conversation transcript ── */}
          {turns.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', letterSpacing: '0.07em', marginBottom: 12 }}>
                CONVERSATION ({turns.length} turn{turns.length !== 1 ? 's' : ''})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {turns.map((turn, i) => (
                  <div key={i} style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: turn.role === 'customer' ? 'flex-start' : 'flex-end',
                  }}>
                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>
                      {turn.role === 'customer' ? '👤 Customer' : '🤖 Agent'}
                    </div>
                    <div style={{
                      maxWidth: '88%', padding: '10px 14px',
                      fontSize: 14,
                      color: '#e2e8f0', lineHeight: 1.65, whiteSpace: 'pre-wrap',
                      borderRadius: turn.role === 'customer' ? '4px 12px 12px 12px' : '12px 4px 12px 12px',
                      background: turn.role === 'customer' ? '#1a1a2e' : 'rgba(124,58,237,0.12)',
                      border: `1px solid ${turn.role === 'customer' ? '#2d2d4e' : 'rgba(124,58,237,0.3)'}`,
                    }}>
                      {turn.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty transcript state */}
          {turns.length === 0 && !evalFailed && (
            <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 13, padding: '20px 0' }}>
              No conversation turns recorded.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Scenario card ────────────────────────────────────────────────────────────

function ScenarioCard({ scenario, onEdit, onDelete, onRun, isRunning, runResult, onViewResult }) {
  const [expanded, setExpanded] = useState(false);

  // Use live result data if available, otherwise fall back to stored scenario data
  const displayResult   = runResult ? runResult.result : scenario.last_result;
  const displayRunCount = runResult ? (scenario.run_count + 1) : scenario.run_count;

  const priority = PRIORITY_META[scenario.priority] || PRIORITY_META.normal;
  const outcome  = OUTCOME_META[scenario.expected_outcome] || OUTCOME_META.resolved;
  const result   = displayResult ? RESULT_META[displayResult] : null;
  const isSecure = (scenario.category || '').startsWith('security_');

  return (
    <div style={{
      background: '#1a1a2e',
      border: `1px solid ${runResult ? (runResult.result === 'passed' ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)') : '#2d2d4e'}`,
      borderRadius: 8,
      padding: '14px 16px',
      marginBottom: 8,
      transition: 'border-color 0.15s',
    }}
      onMouseEnter={e => { if (!runResult) e.currentTarget.style.borderColor = '#7c3aed'; }}
      onMouseLeave={e => { if (!runResult) e.currentTarget.style.borderColor = '#2d2d4e'; }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 14 }}>
              {scenario.name}
            </span>
            {isSecure && (
              <span title="Security test — evaluates resistance to manipulation" style={{
                fontSize: 11, color: '#a78bfa', background: 'rgba(124,58,237,0.15)',
                border: '1px solid rgba(124,58,237,0.3)', borderRadius: 4,
                padding: '1px 6px', cursor: 'help',
              }}>
                🔒 Security
              </span>
            )}
            <Badge label={priority.label} color={priority.color} />
            <Badge label={`→ ${outcome.label}`} color={outcome.color} />
            {result && <Badge label={result.label} color={result.color} bg={result.bg} />}
            {displayRunCount > 0 && (
              <span style={{ fontSize: 11, color: '#6b7280' }}>
                {displayRunCount} run{displayRunCount !== 1 ? 's' : ''}
              </span>
            )}
            {/* View Results button — shown whenever there's a result to view */}
            {runResult && (
              <button
                onClick={() => onViewResult(scenario, runResult)}
                style={{
                  background: 'none', border: '1px solid rgba(124,58,237,0.4)',
                  padding: '2px 10px', fontSize: 11, color: '#a78bfa', cursor: 'pointer',
                  fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4,
                  borderRadius: 4,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              >
                View Results →
              </button>
            )}
          </div>

          {/* Preview / expand */}
          <div
            style={{ color: '#94a3b8', fontSize: 13, cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setExpanded(v => !v)}
          >
            {expanded ? (
              <>
                <span style={{ whiteSpace: 'pre-wrap' }}>{scenario.customer_message}</span>
                <span style={{ color: '#7c3aed', marginLeft: 6, fontSize: 12 }}>
                  <ChevronUp size={12} style={{ verticalAlign: 'middle' }} /> collapse
                </span>
              </>
            ) : (
              <>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 520 }}>
                  {scenario.customer_message}
                </span>
                {scenario.customer_message.length > 80 && (
                  <span style={{ color: '#7c3aed', fontSize: 12 }}>
                    <ChevronDown size={12} style={{ verticalAlign: 'middle' }} /> expand
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            onClick={() => onRun(scenario)}
            disabled={isRunning}
            title="Run this scenario against the active agent config"
            style={{
              background: isRunning ? '#374151' : '#7c3aed',
              color: '#fff', border: 'none', borderRadius: 6,
              padding: '5px 12px', fontSize: 12, fontWeight: 600,
              cursor: isRunning ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            {isRunning
              ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Running…</>
              : <><Play size={12} /> Run</>
            }
          </button>
          <button
            onClick={() => onEdit(scenario)}
            title="Edit scenario"
            style={{
              background: 'transparent', border: '1px solid #374151',
              color: '#94a3b8', borderRadius: 6, padding: '5px 8px',
              cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}
          >
            <Edit3 size={13} />
          </button>
          <button
            onClick={() => onDelete(scenario)}
            title="Delete scenario"
            style={{
              background: 'transparent', border: '1px solid #374151',
              color: '#6b7280', borderRadius: 6, padding: '5px 8px',
              cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#ef4444'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#374151'; e.currentTarget.style.color = '#6b7280'; }}
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ScenarioModal({ scenario, onClose, onSave }) {
  const isEdit = !!scenario?.id;
  const [form, setForm] = useState({
    name: scenario?.name || '',
    category: scenario?.category || 'common',
    customer_message: scenario?.customer_message || '',
    expected_outcome: scenario?.expected_outcome || 'resolved',
    priority: scenario?.priority || 'normal',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.customer_message.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const inputStyle = {
    width: '100%', background: '#0f0f23', border: '1px solid #2d2d4e',
    borderRadius: 6, color: '#e2e8f0', padding: '8px 12px', fontSize: 13,
    outline: 'none', boxSizing: 'border-box',
  };
  const selectStyle = { ...inputStyle };
  const labelStyle = { color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: '#1a1a2e', border: '1px solid #2d2d4e', borderRadius: 12,
        padding: 28, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 16, margin: 0 }}>
            {isEdit ? 'Edit Scenario' : 'New Scenario'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Scenario Name</label>
            <input
              style={inputStyle}
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. Billing Dispute"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Category</label>
              <select style={selectStyle} value={form.category} onChange={e => set('category', e.target.value)}>
                <option value="common">Common Support</option>
                <option value="edge_case">Edge Cases</option>
                <option value="security_pii">Security — PII</option>
                <option value="security_injection">Security — Injection</option>
                <option value="security_policy">Security — Policy</option>
                <option value="security_kb">Security — KB Boundary</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Priority</label>
              <select style={selectStyle} value={form.priority} onChange={e => set('priority', e.target.value)}>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Expected Outcome</label>
            <select style={selectStyle} value={form.expected_outcome} onChange={e => set('expected_outcome', e.target.value)}>
              <option value="resolved">Resolved</option>
              <option value="escalated">Escalated</option>
              <option value="unresolved">Unresolved</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Customer Message</label>
            <textarea
              style={{ ...inputStyle, minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }}
              value={form.customer_message}
              onChange={e => set('customer_message', e.target.value)}
              placeholder="What the customer says to trigger this scenario…"
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: '1px solid #374151',
              color: '#94a3b8', borderRadius: 6, padding: '8px 16px',
              fontSize: 13, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.name.trim() || !form.customer_message.trim()}
            style={{
              background: saving ? '#374151' : '#7c3aed',
              color: '#fff', border: 'none', borderRadius: 6,
              padding: '8px 20px', fontSize: 13, fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {saving ? <RefreshCw size={13} className="spin" /> : <Check size={13} />}
            {isEdit ? 'Save Changes' : 'Create Scenario'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ scenario, onClose, onConfirm }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001,
    }}>
      <div style={{
        background: '#1a1a2e', border: '1px solid #2d2d4e', borderRadius: 12,
        padding: 28, width: 380, maxWidth: '95vw',
      }}>
        <h3 style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 16, marginTop: 0, marginBottom: 10 }}>
          Delete Scenario?
        </h3>
        <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 24 }}>
          <strong style={{ color: '#e2e8f0' }}>"{scenario.name}"</strong> will be permanently deleted.
          {scenario.is_default && (
            <span style={{ color: '#f97316' }}> This is a default scenario — you can re-seed it later from Settings.</span>
          )}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: '1px solid #374151',
              color: '#94a3b8', borderRadius: 6, padding: '8px 16px',
              fontSize: 13, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              background: '#ef4444', color: '#fff', border: 'none',
              borderRadius: 6, padding: '8px 16px', fontSize: 13,
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Security info banner ─────────────────────────────────────────────────────

function SecurityBanner() {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('security_banner_dismissed') === 'true'
  );

  if (dismissed) return null;

  return (
    <div style={{
      background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.25)',
      borderRadius: 8, padding: '12px 16px', marginBottom: 16,
      display: 'flex', alignItems: 'flex-start', gap: 12,
    }}>
      <Shield size={16} style={{ color: '#a78bfa', marginTop: 1, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <span style={{ color: '#c4b5fd', fontWeight: 600, fontSize: 13 }}>Security Scenarios</span>
        <p style={{ color: '#94a3b8', fontSize: 12, margin: '4px 0 0' }}>
          These test whether your AI agent can resist manipulation, protect customer data, and stay within scope.
          OWASP ranks prompt injection as the #1 vulnerability in production AI — present in 73% of deployments.
        </p>
      </div>
      <button
        onClick={() => { setDismissed(true); localStorage.setItem('security_banner_dismissed', 'true'); }}
        style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 0, flexShrink: 0 }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function ScenariosTab({ activeAgentConfig, onUpgradeNeeded }) {
  const { authFetch } = useApi();

  const [scenarios, setScenarios]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [modalScenario, setModalScenario]   = useState(null);
  const [deleteTarget, setDeleteTarget]     = useState(null);
  const [runningId, setRunningId]           = useState(null);
  // Map of scenarioId → run result (so multiple cards can show results independently)
  const [runResults, setRunResults]         = useState({});
  const [toast, setToast]                   = useState(null);
  // Drawer: { scenario, result } | null
  const [drawerData, setDrawerData]         = useState(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchScenarios = useCallback(async () => {
    try {
      const data = await authFetch('/api/scenarios');
      setScenarios(Array.isArray(data) ? data : []);

      if (!Array.isArray(data) || data.length === 0) {
        const result = await authFetch('/api/scenarios/seed', { method: 'POST' });
        const seeded = await authFetch('/api/scenarios');
        setScenarios(Array.isArray(seeded) ? seeded : []);
        if (result.seeded > 0) {
          showToast(`${result.seeded} default scenarios added`, 'success');
        }
      }
    } catch (err) {
      console.error('Failed to load scenarios', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch, showToast]);

  const handleReseed = useCallback(async () => {
    setLoading(true);
    try {
      const result = await authFetch('/api/scenarios/seed', { method: 'POST' });
      const seeded = await authFetch('/api/scenarios');
      setScenarios(Array.isArray(seeded) ? seeded : []);
      showToast(result.seeded > 0 ? `${result.seeded} default scenarios restored` : 'Defaults already present', 'success');
    } catch (err) {
      showToast('Failed to restore defaults', 'error');
    } finally {
      setLoading(false);
    }
  }, [authFetch, showToast]);

  useEffect(() => { fetchScenarios(); }, [fetchScenarios]);

  // ── CRUD handlers ──────────────────────────────────────────────────────────

  const handleCreate = async (form) => {
    const created = await authFetch('/api/scenarios', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    setScenarios(prev => [created, ...prev]);
    setModalScenario(null);
    showToast('Scenario created');
  };

  const handleUpdate = async (form) => {
    const updated = await authFetch(`/api/scenarios/${modalScenario.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    setScenarios(prev => prev.map(s => s.id === updated.id ? updated : s));
    setModalScenario(null);
    showToast('Scenario updated');
  };

  const handleDelete = async () => {
    await authFetch(`/api/scenarios/${deleteTarget.id}`, { method: 'DELETE' });
    setScenarios(prev => prev.filter(s => s.id !== deleteTarget.id));
    setRunResults(prev => { const n = { ...prev }; delete n[deleteTarget.id]; return n; });
    // Close drawer if the deleted scenario is open
    if (drawerData?.scenario?.id === deleteTarget.id) setDrawerData(null);
    setDeleteTarget(null);
    showToast('Scenario deleted', 'neutral');
  };

  const handleRun = async (scenario) => {
    if (!activeAgentConfig) {
      showToast('No active agent config. Set one in Agent Config tab.', 'error');
      return;
    }
    setRunningId(scenario.id);
    // Clear any previous result for this card
    setRunResults(prev => { const n = { ...prev }; delete n[scenario.id]; return n; });

    try {
      const result = await authFetch(`/api/scenarios/${scenario.id}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_config_id: activeAgentConfig.id }),
      });

      // Store result in map — card will show "View Results" button
      setRunResults(prev => ({ ...prev, [scenario.id]: result }));

      // Also update the scenario in state so badge persists across re-renders
      setScenarios(prev => prev.map(s =>
        s.id === scenario.id
          ? { ...s, last_result: result.result, run_count: s.run_count + 1 }
          : s
      ));

      // Auto-open the drawer with the fresh result
      setDrawerData({ scenario, result });

      showToast(`"${scenario.name}" — ${result.result === 'passed' ? '✓ Passed' : '✗ Failed'}`);
    } catch (err) {
      if (err.status === 402) {
        onUpgradeNeeded && onUpgradeNeeded();
      } else {
        showToast('Run failed — check agent config', 'error');
      }
    } finally {
      setRunningId(null);
    }
  };

  // ── Filtered view ──────────────────────────────────────────────────────────

  const visible = activeCategory === 'all'
    ? scenarios
    : scenarios.filter(s => s.category === activeCategory);

  const counts = CATEGORIES.reduce((acc, cat) => {
    acc[cat.id] = cat.id === 'all'
      ? scenarios.length
      : scenarios.filter(s => s.category === cat.id).length;
    return acc;
  }, {});

  const showSecurityBanner = ['all', 'security_pii', 'security_injection', 'security_policy', 'security_kb']
    .includes(activeCategory) && scenarios.some(s => s.category.startsWith('security_'));

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px 28px', maxWidth: 900, position: 'relative' }}>

      {/* Keyframes */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes cardEntrance { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 2000,
          background: toast.type === 'error' ? '#7f1d1d' : toast.type === 'neutral' ? '#1f2937' : '#14532d',
          color: '#e2e8f0', borderRadius: 8, padding: '10px 18px',
          fontSize: 13, fontWeight: 500, boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          border: `1px solid ${toast.type === 'error' ? '#ef4444' : toast.type === 'neutral' ? '#374151' : '#16a34a'}40`,
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h2 style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 20, margin: 0 }}>Scenarios</h2>
          <p style={{ color: '#6b7280', fontSize: 13, margin: '4px 0 0' }}>
            {scenarios.length} scenario{scenarios.length !== 1 ? 's' : ''} —{' '}
            {activeAgentConfig ? (
              <>
                testing against{' '}
                <span style={{ color: '#a78bfa', fontWeight: 600 }}>
                  {activeAgentConfig.name}
                </span>
              </>
            ) : (
              <span style={{ color: '#f97316' }}>no agent configured</span>
            )}
          </p>
        </div>
        <button
          onClick={() => setModalScenario({})}
          style={{
            background: '#7c3aed', color: '#fff', border: 'none',
            borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <Plus size={14} /> New Scenario
        </button>
      </div>

      {/* Category filter tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            title={cat.isSecure ? 'Security scenario — tests AI resistance to manipulation and policy boundary violations' : undefined}
            style={{
              background: activeCategory === cat.id ? '#7c3aed' : 'transparent',
              color: activeCategory === cat.id ? '#fff' : '#94a3b8',
              border: `1px solid ${activeCategory === cat.id ? '#7c3aed' : '#2d2d4e'}`,
              borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 500,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
              transition: 'all 0.15s',
            }}
          >
            {cat.icon && <span>{cat.icon}</span>}
            {cat.label}
            {counts[cat.id] > 0 && (
              <span style={{
                background: activeCategory === cat.id ? 'rgba(255,255,255,0.2)' : '#1f1f3a',
                borderRadius: 10, padding: '1px 6px', fontSize: 10,
              }}>
                {counts[cat.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Security banner */}
      {showSecurityBanner && <SecurityBanner />}

      {/* No active config warning */}
      {!activeAgentConfig && (
        <div style={{
          background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.25)',
          borderRadius: 8, padding: '10px 14px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <AlertTriangle size={14} style={{ color: '#f97316', flexShrink: 0 }} />
          <span style={{ color: '#94a3b8', fontSize: 13 }}>
            No agent configured. Go to <strong style={{ color: '#e2e8f0' }}>Agent Config</strong> and mark one as <strong style={{ color: '#10b981' }}>Active</strong> — that config defines the AI agent your scenarios will test against.
          </span>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ color: '#6b7280', fontSize: 14, textAlign: 'center', padding: '40px 0' }}>
          Loading scenarios…
        </div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280', fontSize: 14 }}>
          {activeCategory === 'all' ? (
            <div>
              <p style={{ margin: '0 0 16px' }}>No scenarios yet.</p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setModalScenario({})}
                  style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  + New Scenario
                </button>
                <button
                  onClick={handleReseed}
                  style={{ background: 'transparent', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.4)', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  ↺ Restore 38 defaults
                </button>
              </div>
            </div>
          ) : (
            'No scenarios in this category. Add one or switch to a different filter.'
          )}
        </div>
      ) : (
        visible.map((s, i) => (
          <div
            key={s.id}
            style={{
              animation: 'cardEntrance 0.3s ease-out both',
              animationDelay: `${Math.min(i * 40, 400)}ms`,
            }}
          >
            <ScenarioCard
              scenario={s}
              onEdit={s => setModalScenario(s)}
              onDelete={s => setDeleteTarget(s)}
              onRun={handleRun}
              isRunning={runningId === s.id}
              runResult={runResults[s.id] || null}
              onViewResult={(scenario, result) => setDrawerData({ scenario, result })}
            />
          </div>
        ))
      )}

      {/* Modals */}
      {modalScenario !== null && (
        <ScenarioModal
          scenario={modalScenario.id ? modalScenario : null}
          onClose={() => setModalScenario(null)}
          onSave={modalScenario.id ? handleUpdate : handleCreate}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          scenario={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}

      {/* Slide-out results drawer */}
      {drawerData && (
        <ScenarioResultDrawer
          key={drawerData.scenario.id + '_' + Date.now()}
          result={drawerData.result}
          scenarioName={drawerData.scenario.name}
          onClose={() => setDrawerData(null)}
        />
      )}
    </div>
  );
}
